import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  makeStyles,
  Box,
} from '@material-ui/core';
import pricingData from 'lib/Api/data/pricing.json';
import pricingDataOld from 'lib/Api/data/pricing_old.json';

const useStyles = makeStyles((theme) => ({
  tableHeader: {
    backgroundColor: theme.palette.primary.main,
  },
  tableHeaderCell: {
    color: theme.palette.primary.contrastText,
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
  },
  sectionTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontWeight: 500,
  },
  tierHeader: {
    textAlign: 'center',
    fontWeight: 'bold',
  },
  priceCell: {
    textAlign: 'center',
    fontSize: '0.9rem',
  },
  setupPrice: {
    display: 'block',
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  dialogContent: {
    paddingBottom: theme.spacing(3),
  },
  controls: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

interface PricingTableDialogProps {
  open: boolean;
  onClose: () => void;
}

const PricingTableDialog: React.FC<PricingTableDialogProps> = ({
  open,
  onClose,
}) => {
  const classes = useStyles();
  const [useOldPricing, setUseOldPricing] = React.useState(false);

  const data = useOldPricing ? pricingDataOld : pricingData;
  const tiers = ['tier_1', 'tier_2', 'tier_3', 'tier_4'] as const;

  // Get all unique module keys across all tiers
  const moduleKeys = Array.from(
    new Set(
      tiers.flatMap((tier) =>
        data.pricing[tier].addons.map((addon: any) => addon.module),
      ),
    ),
  );

  // Sort modules: put specific ones first for better readability if needed, or just alphabetical
  // Let's try to group them nicely: Software first, then Hardware?
  // We can use the 'module_type' from metadata if available (mapped by module key)

  const getModuleMetadata = (key: string) => {
    // @ts-ignore
    return data.modules[key];
  };

  const sortedModuleKeys = moduleKeys.sort((a, b) => {
    // priority order: package, tracking, addon, hardware
    const typeOrder = { package: 1, tracking: 2, addon: 3, hardware: 4 };
    const metaA = getModuleMetadata(a);
    const metaB = getModuleMetadata(b);

    const typeA = metaA?.module_type || 'addon'; // default to addon
    const typeB = metaB?.module_type || 'addon';

    // @ts-ignore
    if (typeOrder[typeA] !== typeOrder[typeB]) {
      // @ts-ignore
      return (typeOrder[typeA] || 99) - (typeOrder[typeB] || 99);
    }

    // Secondary sort by key or title
    return a.localeCompare(b);
  });

  const getTierInfo = (tier: string) => {
    // @ts-ignore
    const t = data.tiers[tier];
    return `${t.minEmployees}-${t.maxEmployees} users`;
  };

  const formatPrice = (price: number | undefined) => {
    if (price === undefined || price === null) return '-';
    return `€${price.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Pricing Table</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Box className={classes.controls}>
          <FormControlLabel
            control={
              <Switch
                checked={useOldPricing}
                onChange={(e) => setUseOldPricing(e.target.checked)}
                color="primary"
              />
            }
            label={`Show Old Pricing`}
          />
        </Box>

        <Typography variant="h6" className={classes.sectionTitle}>
          Feature & Module Pricing
        </Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead className={classes.tableHeader}>
              <TableRow>
                <TableCell className={classes.tableHeaderCell}>
                  Module
                </TableCell>
                {tiers.map((tier) => (
                  <TableCell
                    key={tier}
                    className={classes.tableHeaderCell}
                    align="center"
                  >
                    <div>{tier.replace('_', ' ').toUpperCase()}</div>
                    <div style={{ fontSize: '0.75rem', fontWeight: 'normal' }}>
                      {getTierInfo(tier)}
                    </div>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedModuleKeys.map((moduleKey) => {
                const metadata = getModuleMetadata(moduleKey);
                const title = metadata?.title || moduleKey;

                return (
                  <TableRow key={moduleKey}>
                    <TableCell component="th" scope="row">
                      <Typography variant="body2" style={{ fontWeight: 500 }}>
                        {title}
                      </Typography>
                    </TableCell>
                    {tiers.map((tier) => {
                      const tierData = data.pricing[tier].addons.find(
                        (a: any) => a.module === moduleKey,
                      );

                      if (!tierData) {
                        return (
                          <TableCell key={tier} className={classes.priceCell}>
                            -
                          </TableCell>
                        );
                      }

                      const price =
                        tierData.clocking_pro_price ?? tierData.price;
                      const setup = tierData.setup_price || 0;
                      const setupLabel =
                        metadata?.module_type === 'hardware'
                          ? 'installation'
                          : 'setup';

                      return (
                        <TableCell key={tier} className={classes.priceCell}>
                          <div>
                            {price === 0
                              ? 'Included'
                              : `${formatPrice(price)}${
                                  metadata?.module_type === 'hardware' &&
                                  setup > 0
                                    ? ' device only'
                                    : ''
                                }`}
                          </div>
                          {setup > 0 && (
                            <span className={classes.setupPrice}>
                              + {formatPrice(setup)} {setupLabel}
                            </span>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="h6" className={classes.sectionTitle}>
          Extras
        </Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead className={classes.tableHeader}>
              <TableRow>
                <TableCell className={classes.tableHeaderCell}>Item</TableCell>
                <TableCell className={classes.tableHeaderCell} align="right">
                  Unit Price
                </TableCell>
                <TableCell className={classes.tableHeaderCell} align="right">
                  Setup Fee
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.extras.map((extra: any) => (
                <TableRow key={extra.key}>
                  <TableCell component="th" scope="row">
                    {extra.title}
                  </TableCell>
                  <TableCell align="right">
                    {extra.price === 0 ? 'Free' : formatPrice(extra.price)}
                  </TableCell>
                  <TableCell align="right">
                    {extra.setup_price === 0
                      ? '-'
                      : formatPrice(extra.setup_price)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PricingTableDialog;
